<template>
  <div class="row">
    <div v-if="!loading" class="storage-index-container col-12">
      <f-data-table :id="eventTarget"
                    :data-uri="data_uri"
                    :columns="columns"
                    :pagination="{
                      enabled: true,
                      per_page: 25,
                    }"
                    :static-params="[
                      {
                        field: 'with_items',
                        value: true,
                      }
                    ]"
                    :pre-filter="pre_filter"
                    :server-side="true"
                    :heading="title"
                    :selectable-rows="true"
                    :expanded="portletExpanded"
                    :icon="icon"
                    :locked="portletLocked">
        <template #_actions="props">
          <button type="button" class="btn btn-ss-standout btn-icon-sm" @click="showUploadModal">
            <i class="la la-upload" /> Upload New Storage
          </button>
          <button type="button" class="btn btn-ss btn-icon-sm" @click="() => props.reloadData()">
            <i class="flaticon2-refresh" />Reload Data
          </button>
        </template>
        <template #_filters>
          <f-data-table-filters :target="eventTarget"
                                :accordion="accordion"
                                :expanded="true"
                                :filters="[{
                                  name: `name`,
                                  display: `Name`,
                                  prepend: `la la-search`,
                                  placeholder: `Search names...`,
                                }]">
            <template #unused="props">
              <label class="hover-tooltip" for="unused_filter" title="Unused items are those which have been uploaded, but aren't being used by any Item, Feed, etc.">Unused?:</label>
              <multiselect v-model="unused_filter_multi_select_val"
                           name="unused-filter-select"
                           :options="[`Only`, `Exclude`]"
                           placeholder="Select a status..."
                           @input="(selected) => {
                             selected = selected ? selected.toLowerCase() : null;
                             props.filterChange({
                               server: {
                                 type: `param`,
                                 field: `unused`,
                                 value: selected,
                               },
                             });
                           }" />
            </template>
          </f-data-table-filters>
        </template>
        <template #_row_controls="props">
          <button v-perms="['web.storage.download']" class="btn btn-ss" @click="props.do({cb: downloadAssets})">
            <i class="fa fa-download" />Download
          </button>
          <button v-perms="['api.storage.destroy']" class="btn btn-ss-danger" @click="props.do({cb: deleteAssets, reload_data: true})">
            <i class="flaticon2-warning" />Delete
          </button>
        </template>
        <template #file_size="props">
          <span>{{ formatBytesAs(props.row.file_size, `MB`) }}</span>
        </template>
        <template #created_at="props">
          <span>{{ formatDateTime(props.row.created_at) }}</span>
        </template>
        <template #related_item="props">
          <a v-if="props.row.item"
             :href="isPodcaster() ? `/episode/${props.row.item.uuid}/edit` : `/items/${props.row.item.uuid}/edit`"
             class="font-weight-bold">
            <i class="la la-arrow-right" style="margin-right: -0.3rem;" /> {{ props.row.item.name || props.row.item.uuid }}
          </a>
          <span v-else class="muted no-content-notice">None</span>
        </template>
        <template #actions="props">
          <div class="row-action-cell">
            <div class="btn-group">
              <a class="btn btn-sm btn-ss" :href="`/storage/${props.row.uuid}/edit`">
                <i class="la la-edit" />Edit
              </a>
              <button type="button" class="btn btn-sm btn-ss dropdown-toggle dropdown-toggle-split"
                      data-toggle="dropdown"
                      aria-haspopup="true" aria-expanded="false">
                <span class="sr-only">Toggle Dropdown</span>
              </button>
              <div class="dropdown-menu">
                <button v-perms="['web.storage.download']" class="dropdown-item"
                        @click="() => {
                          props.do({ cb: downloadAssets });
                        }">
                  <i class="la la-download" /> Download
                </button>
                <button v-perms="['api.storage.destroy']" class="dropdown-item"
                        @click="() => {
                          props.do({ cb: deleteAssets });
                        }">
                  <i class="la la-trash" /> Delete
                </button>
                <button class="dropdown-item"
                        @click="initQuickCreateModal(props.row)">
                  <i class="la la-file-audio-o" /> Create {{ getItemLabel(true) }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </f-data-table>
    </div>
    <delete-confirm-modal name="delete-confirm"
                          :title="`Deleting ${assets_to_delete.length} Storage Assets`"
                          :entities-to-delete="assets_to_delete"
                          entity-type="storage asset"
                          :width="'70vw'">
      <f-data-table v-bind="deletion_confirm_table">
        <template #asset="props">
          <a :href="`/storage/${props.row.asset.uuid}/edit`"
             class="font-weight-bold"
             target="_blank">
            <i class="la la-arrow-right" style="margin-right: -0.3rem;" /> {{ props.row.asset.name || props.row.asset.uuid }}
          </a>
        </template>
        <template #item="props">
          <a v-if="props.row.item"
             :href="`/items/${props.row.item.uuid}/edit`"
             class="font-weight-bold"
             target="_blank">
            <i class="la la-arrow-right" style="margin-right: -0.3rem;" /> {{ props.row.item.name || props.row.item.uuid }}
          </a>
          <span v-else>None</span>
        </template>
      </f-data-table>
      <div class="d-flex flex-column align-items-center">
        <ss-checkbox v-if="deletion_confirm_table.data.find(row => row.item)"
                     id="also-delete-items"
                     v-model="also_delete_items_on_asset_delete">
          <span class="ml-2">
            Also delete associated Items?
            <span v-tooltip="`Deleting a storage asset which is being used by an Item causes that Item to become 'orphaned'; leaving it with no associated asset. Tick this to delete those Items instead.`">
              <i class="ml-2 cursor-pointer fa fa-info-circle" />
            </span>
          </span>
        </ss-checkbox>
      </div>
    </delete-confirm-modal>
    <confirm-modal id="orphan-modal"
                   name="orphan-warning"
                   title="Warning">
      <h4 class="text-center">
        Some items may be <span class="hover-tooltip" title="Orphaned items are items whos Storage file is missing (usually deleted).">orphaned!</span>
      </h4>
      <ul>
        <li v-for="asset in used_assets_for_deletion" :key="asset.storage_uuid">
          <collapse-text :text="asset.item.item_name">
            <strong><i class="flaticon2-warning" /> Uses storage asset:</strong>
            <p class="d-flex justify-content-center my-4">
              {{ asset.storage_name }}
            </p>
            <template v-if="asset.item.feeds && asset.item.feeds.length > 0">
              <hr>
              <strong><i class="flaticon2-warning" /> Is also used by {{ getFeedLabel() }}s:</strong>
              <ul>
                <li v-for="feed in asset.item.feeds" :key="feed.uuid">
                  <a :href="`/feeds/${feed.uuid}/edit`">{{ feed.name }}</a>
                </li>
              </ul>
            </template>
          </collapse-text>
        </li>
      </ul>
    </confirm-modal>

    <vjs-modal name="item-create-modal"
               :title="`Create ${getItemLabel(true)}`"
               icon="la la-plus-circle">
      <item-creation-form :preselected-storage-asset="quick_create_asset" />
    </vjs-modal>

    <vjs-modal name="storage-upload-modal"
               title="Upload New Storage Assets"
               icon="la la-upload"
               width="800"
               :click-to-close="false"
               :close-button="show_upload_modal_close"
               @opened="autoOpenUploaderDialogue">
      <uploader-dropzone post-url="/api/v1/storage"
                         :upload-button-classes="[`btn`, `btn-ss`]"
                         mime-types=".mp3"
                         @uploading-progress="show_upload_modal_close = false"
                         @success="finishUploadModal"
                         @cancelled-multiple="finishUploadModal" />
    </vjs-modal>
  </div>
</template>

<script>
import { waitForElements } from "Helpers/dom-manip.js";

import FDataTable from 'VuePartials/App/Storage/FDataTable/FDataTable.vue';
import FDataTableFilters from 'VuePartials/App/Storage/FDataTable/FDataTableFilters.vue';
import DeleteConfirmModal from 'VueModals/DeleteConfirmModal';
import ConfirmModal from 'VueModals/ConfirmModal';
import CollapseText from 'VueComponents/CollapseText.vue';
import ItemQuickCreateMixin from "VueMixins/ItemQuickCreateMixin";
import FDataTableMixin from 'VueMixins/FDataTableMixin';
import ItemCreationForm from "VueComponents/ItemCreation/Form.vue";
import VjsModal from 'VueModals/VjsModal';
import UploaderDropzone from "VuePartials/UploaderDropzone";
import { chunked } from 'Lib/array-util';
import { isPodcaster } from 'Helpers/misc';

/** Max number of Items ids to send in query params, i.e for `mass-delete`. */
const API_MASS_ACTION_LIMIT = 16;

export default {
  components: {
    FDataTable,
    FDataTableFilters,
    DeleteConfirmModal,
    ConfirmModal,
    CollapseText,
    ItemCreationForm,
    VjsModal,
    UploaderDropzone,
  },
  mixins: [FDataTableMixin, ItemQuickCreateMixin],
  props: {
    title: {
      type: String,
      required: false,
      default: `Storage`,
    },
    preFilter: {
      type: Array,
      required: false,
      default: null,
    },
    icon: {
      type: String,
      required: false,
      default: `flaticon2-download`,
    },
    eventTarget: {
      type: String,
      required: false,
      default: `storage-table`,
    },
  },
  data: () => ({
    show_upload_modal_close: true,
    also_delete_items_on_asset_delete: false,
    assets_to_delete: [],
    loading: true,
    accordion: `storage-index`,
    data_uri: '/api/v1/storage',
    columns: [
      {
        field: `name`,
        display: `Name`,
      },
      {
        field: `file_size`,
        display: `File Size`,
        toggleable: true,
      },
      {
        field: `created_at`,
        display: `Created At`,
        active_sort: true,
        toggleable: true,
      },
      {
        field: 'related_item',
        display: 'Related Item',
        sortable: false,
        toggleable: true,
      },
      {
        field: `actions`,
        display: `Actions`,
        sortable: false,
        width: 150,
        never_clip: true,
      },
    ],
    unused_filter_multi_select_val: ``,
    pre_filter: [],
    used_assets_for_deletion: [],
    selected_assets: [],
    preferences: null,
    deletion_confirm_table: {
      id: 'deletion-confirm-table',
      showHeading: false,
      icon: 'la la-trash',
      pagination: {
        enabled: false,
      },
      columns: [
        {
          field: 'asset',
          display: 'Asset',
        },
        {
          field: 'item',
          display: 'Item',
        },
      ],
      data: [],
    },
  }),
  watch: {
    assets_to_delete(new_val) {
      this.deletion_confirm_table.data = new_val.map(asset => ({
        asset,
        item: asset.item,
      }));
    },
  },
  mounted() {
    EventBus.$on(`fdt-rows-selected`, (ev) => {
      this.selected_assets = ev;
    });
    // if preFilters not provided, use url params
    // else use the provided filts
    if (!this.preFilter || this.preFilter.length === 0) {
      const unused_param = new URLSearchParams(window.location.search).get(`unused`);
      this.unused_filter_multi_select_val = unused_param && [
        `only`,
        `exclude`,
      ].includes(unused_param.toLowerCase()) ? `${unused_param.charAt(0).toUpperCase()}${unused_param.slice(1)}` : ``;
      this.pre_filter = [
        {
          type: `param`,
          field: `unused`,
          value: this.unused_filter_multi_select_val.toLowerCase() || null,
        },
      ];
    } else {
      this.pre_filter = this.preFilter;
    }
    this.loading = false;
  },
  methods: {
    finishUploadModal() {
      this.hideModal('storage-upload-modal');
      this.reloadStorageTable();
      this.show_upload_modal_close = true;
    },
    isPodcaster,
    /**
     * Here we simulate a click on the <uploader-dropzone>'s main button control
     */
    async autoOpenUploaderDialogue() {
      const [btn_el] = await waitForElements('#fileinput-button');
      if (btn_el) btn_el.click();
    },
    async showUploadModal() {
      await this.fireModal("storage-upload-modal");
    },
    reloadStorageTable() {
      EventBus.$emit(`${this.eventTarget}-reload`);
    },
    handleResponse(res, success_message, error_message = `Error`) {
      if (res.data && res.data.success) {
        toastr.success(success_message || res.data.message || `Success!`);
      } else {
        if (res.error.message) {
          error_message = `${error_message}: ${res.error.message}`;
        }
        toastr.error(error_message || `Something went wrong!`);
      }
    },
    async doItemAJAXAction(ids, method, options) {
      const config = {
        endpoint: ``,
        success_message: `Success!`,
        error_message: `Error`,
        ...options,
      };
      try {
        const res = await method(`${this.data_uri}${config.endpoint}`, {
          params: {
            selected: ids.join(`,`),
          },
        });
        this.handleResponse(res, config.success_message, config.error_message);
      } catch (err) {
        console.error(err);
        window.handleAxiosError(err);
      }
    },
    editAsset(id) {
      window.location.href = `/storage/${id}/edit`;
    },
    downloadAssets(...assets) {
      const ids = assets.map(asset => asset.uuid);
      window.location.href = `/storage/massDownload?selected=${ids.join(`,`)}`;
      return false;
    },
    async deleteAssets(...assets_to_delete) {
      this.assets_to_delete = assets_to_delete;
      const res = await this.fireModal(`delete-confirm`);

      if (!res.data.confirm) {
        return false;
      }

      // here we deal with warning the user that deleting assets can leave associated items 'orphaned',
      // unless the user specified they want to delete those items also
      if (!this.also_delete_items_on_asset_delete) {
        // we need to re-fetch here so we can also grab the associated feeds
        const assets_with_items = (await axios.get(`${this.data_uri}/is-used`, {
          params: {
            storage_ids: assets_to_delete.map(asset => asset.uuid).join(`,`),
          },
        })).data.data?.usage;

        // only show if at least one targeted asset has an associated item
        if (assets_with_items?.length > 0) {
          this.used_assets_for_deletion = assets_with_items;
          const orphan_modal_res = await this.fireModal(`orphan-warning`);
          if (!orphan_modal_res.data.confirm) {
            return false;
          }
        }
      }

      // chunking for the API if we're trying to delete more assets than `API_MASS_ACTION_LIMIT`
      const chunked_assets = chunked(assets_to_delete, API_MASS_ACTION_LIMIT);

      const url = new URL(`${window.location.origin}/api/v1/storage/massDelete`);
      if (this.also_delete_items_on_asset_delete) {
        url.searchParams.append('delete_items', true);
      }

      const responses = await Promise.all(chunked_assets.map(async (chunk) => {
        const uuids_str = chunk.map(asset => asset.uuid).join(',');

        url.searchParams.set('selected', uuids_str);
        const href = url.href;

        try {
          const response = await axios.delete(href);

          return response;
        } catch (err) {
          window.handleAxiosError(err);
          return false;
        }
      }));

      const all_success = responses.every(response => response.data?.success === true);

      // if all went well, just announce it and fast return
      if (all_success) {
        toastr.success(`Successfully deleted ${assets_to_delete.length} Assets.`);
        return true;
      }

      // otherwise we'll organise the successes and errors and show the errors individually, as well as a warning about partial success

      const succeeded = responses.filter(response => response.data?.success);
      const errored = responses.filter(response => response.data?.error?.message);

      errored.forEach(err_response => toastr.error(err_response.data?.error?.message));

      if (errored.length && succeeded.length) {
        toastr.warning('Deletions partially successful; some Assets were not deleted.');
      }
    },
  },
};

</script>

<style lang="scss" scoped>
    .btn.unused {
        margin-bottom:1em;
    }
    .tbl-filter-item {
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: 0 20%;

      label {
        font-weight: bold;
      }

      .form-control {
        width: 100%;
        padding: 0.6em 0.3em !important;
      }
    }
    .row-action-cell {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
</style>
